import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { ChevronLeft, ChevronRight, MessageCircle, ShoppingBag, ArrowLeft } from 'lucide-react';
import { resolveImgUrl } from '@/utils/imgUrl';
import { motion } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function TokoProdukDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [produk, setProduk] = useState(location.state?.produk || null);
  const [settings, setSettings] = useState({});
  const [currentPhoto, setCurrentPhoto] = useState(0);
  const touchStartX = useRef(null);

  useEffect(() => {
    if (!produk) {
      axios.get(`${API}/produk/${id}`)
        .then(r => setProduk(r.data))
        .catch(() => navigate('/toko', { replace: true }));
    }
    axios.get(`${API}/settings`).then(r => setSettings(r.data)).catch(() => {});
  }, [id]);

  if (!produk) return (
    <Layout>
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    </Layout>
  );

  // Build photos: fotos[] first, fallback to foto
  const photos = (produk.fotos && produk.fotos.length > 0)
    ? produk.fotos
    : (produk.foto ? [produk.foto] : []);

  const rupiah = (n) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(n);

  const handleOrder = () => {
    const wa = produk.whatsapp || settings.whatsapp_pesantren || '6281234567890';
    const msg = encodeURIComponent(`Assalamualaikum, saya ingin memesan produk:\n*${produk.nama}*\nHarga: ${rupiah(produk.harga)}`);
    window.open(`https://wa.me/${wa}?text=${msg}`, '_blank');
  };

  const prevPhoto = () => setCurrentPhoto(i => (i - 1 + photos.length) % photos.length);
  const nextPhoto = () => setCurrentPhoto(i => (i + 1) % photos.length);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) { diff > 0 ? nextPhoto() : prevPhoto(); }
    touchStartX.current = null;
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 pb-28 md:pb-10">
        {/* Back bar */}
        <div className="bg-white border-b border-gray-100 px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
          <button
            data-testid="produk-detail-back-btn"
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <ArrowLeft size={18} className="text-gray-600" />
          </button>
          <span className="font-semibold text-gray-800 text-sm truncate flex-1">{produk.nama}</span>
        </div>

        {/* Slideshow */}
        <div
          className="relative bg-gray-100 select-none"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {photos.length > 0 ? (
            <motion.img
              key={currentPhoto}
              src={resolveImgUrl(photos[currentPhoto])}
              alt={produk.nama}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.2 }}
              className="w-full h-72 md:h-96 object-cover"
              data-testid="produk-detail-photo"
            />
          ) : (
            <div className="w-full h-72 md:h-96 bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center">
              <ShoppingBag size={60} className="text-emerald-300" />
            </div>
          )}

          {/* Prev / Next — only if >1 photo */}
          {photos.length > 1 && (
            <>
              <button
                onClick={prevPhoto}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/30 hover:bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={nextPhoto}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-black/30 hover:bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all"
              >
                <ChevronRight size={18} />
              </button>

              {/* Dot indicators */}
              <div
                data-testid="produk-slideshow-dots"
                className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5"
              >
                {photos.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentPhoto(i)}
                    className={`rounded-full transition-all duration-200 ${
                      i === currentPhoto ? 'w-5 h-2 bg-white' : 'w-2 h-2 bg-white/60 hover:bg-white/80'
                    }`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Info */}
        <div className="px-4 md:px-0 py-4 md:py-6 max-w-lg md:mx-auto space-y-4">
          {/* Name, price */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-5 shadow-card"
          >
            <span className="text-[10px] text-emerald-600 font-bold uppercase tracking-widest">
              {produk.kategori}
            </span>
            <h1 className="font-playfair text-xl font-bold text-gray-900 mt-1 leading-snug">
              {produk.nama}
            </h1>
            <p className="text-2xl font-bold text-emerald-700 mt-2">
              {rupiah(produk.harga)}
            </p>
            {produk.stok != null && (
              <p className="text-xs text-gray-400 mt-1">Stok: {produk.stok}</p>
            )}
          </motion.div>

          {/* Description */}
          {produk.deskripsi && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 }}
              className="bg-white rounded-2xl p-5 shadow-card"
            >
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Deskripsi</h3>
              <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
                {produk.deskripsi}
              </p>
            </motion.div>
          )}

          {/* Order button */}
          <motion.button
            data-testid="produk-detail-order-btn"
            onClick={handleOrder}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12 }}
            className="w-full flex items-center justify-center gap-2.5 bg-emerald-500 hover:bg-emerald-600 active:scale-[0.98] text-white py-4 rounded-2xl font-semibold text-base shadow-emerald transition-all"
          >
            <MessageCircle size={18} /> Pesan via WhatsApp
          </motion.button>
        </div>
      </div>
    </Layout>
  );
}
